import { LayerData, tLayerSettings } from '@/atoms';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import * as WebIFC from 'web-ifc';

const ifcApi = new WebIFC.IfcAPI();
ifcApi.SetWasmPath('/');
const initPromise = ifcApi.Init();

interface BatchedMeshData {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  id: string;
  layerId: string;
  origin: THREE.Vector3;
}

interface PendingGeometry {
  geometry: THREE.BufferGeometry;
  translation: THREE.Vector3;
  materialKey: string;
}

interface MaterialCache {
  [key: string]: THREE.Material;
}

const MERGE_CHUNK_SIZE = 500;

export const IFC = ({ layers }: { layers: LayerData[] }) => {
  const [batchedMeshes, setBatchedMeshes] = useState<BatchedMeshData[]>([]);
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const materials = useMemo<MaterialCache>(() => ({}), []);

  const ifcGeometryToBuffer = useCallback((color: WebIFC.Color, vertexData: Float32Array, indexData: Uint32Array) => {
    const geometry = new THREE.BufferGeometry();
    const vertexCount = vertexData.length / 2;

    const posFloats = new Float32Array(vertexCount);
    const normFloats = new Float32Array(vertexCount);
    const colorFloats = new Float32Array(vertexCount);

    for (let i = 0; i < vertexData.length; i += 6) {
      const idx = i / 2;
      posFloats[idx] = vertexData[i];
      posFloats[idx + 1] = vertexData[i + 1];
      posFloats[idx + 2] = vertexData[i + 2];

      normFloats[idx] = vertexData[i + 3];
      normFloats[idx + 1] = vertexData[i + 4];
      normFloats[idx + 2] = vertexData[i + 5];

      colorFloats[idx] = color.x;
      colorFloats[idx + 1] = color.y;
      colorFloats[idx + 2] = color.z;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(posFloats, 3));
    geometry.setAttribute('normal', new THREE.BufferAttribute(normFloats, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colorFloats, 3));
    geometry.setIndex(new THREE.BufferAttribute(indexData, 1));
    return geometry;
  }, []);

  const getMeshMaterial = useCallback(
    (color: WebIFC.Color, layerSetting: tLayerSettings) => {
      let c = color;
      if (c.x === 1 && c.y === 1 && c.z === 1 && c.w === 1) {
        const threeColor = new THREE.Color(layerSetting.modelDefaultColor);
        c = { x: threeColor.r, y: threeColor.g, z: threeColor.b, w: 1 };
      }

      const colID = `${c.x}${c.y}${c.z}${c.w}${layerSetting.modelIsWireframe}`;
      if (materials[colID]) return materials[colID];

      const material = new THREE.MeshPhongMaterial({
        color: new THREE.Color(c.x, c.y, c.z),
        side: THREE.DoubleSide,
        vertexColors: true,
        wireframe: layerSetting.modelIsWireframe,
        transparent: c.w !== 1,
        opacity: c.w !== 1 ? c.w : 1,
      });

      materials[colID] = material;
      return material;
    },
    [materials],
  );

  const getBufferGeometry = useCallback(
    (modelID: number, placedGeometry: WebIFC.PlacedGeometry) => {
      const geometry = ifcApi.GetGeometry(modelID, placedGeometry.geometryExpressID);
      const verts = ifcApi.GetVertexArray(geometry.GetVertexData(), geometry.GetVertexDataSize());
      const indices = ifcApi.GetIndexArray(geometry.GetIndexData(), geometry.GetIndexDataSize());
      const bufferGeometry = ifcGeometryToBuffer(placedGeometry.color, verts, indices);

      if ('delete' in geometry && typeof geometry.delete === 'function') {
        geometry.delete();
      }
      return bufferGeometry;
    },
    [ifcGeometryToBuffer],
  );

  const processPlacedGeometry = useCallback(
    (modelID: number, placedGeometry: WebIFC.PlacedGeometry, layerSetting: tLayerSettings) => {
      const rawGeometry = getBufferGeometry(modelID, placedGeometry);

      // Decompose transformation matrix
      const matrix = new THREE.Matrix4().fromArray(placedGeometry.flatTransformation);
      const translation = new THREE.Vector3();
      const rotation = new THREE.Quaternion();
      const scale = new THREE.Vector3();
      matrix.decompose(translation, rotation, scale);

      // Convert to Y-up coordinate system
      const convertedTranslation = new THREE.Vector3(translation.x, -translation.z, translation.y);

      // Apply rotation/scale to geometry
      const rotScaleMatrix = new THREE.Matrix4().compose(new THREE.Vector3(), rotation, scale);
      rawGeometry.applyMatrix4(rotScaleMatrix).rotateX(Math.PI / 2);

      // Get material key
      const { color } = placedGeometry;
      const materialKey = `${color.x}-${color.y}-${color.z}-${color.w}`;

      return {
        geometry: rawGeometry,
        translation: convertedTranslation,
        materialKey,
        material: getMeshMaterial(color, layerSetting),
      };
    },
    [getBufferGeometry, getMeshMaterial],
  );

  const mergeGeometriesByMaterial = useCallback(
    (
      pendingGeometries: PendingGeometry[],
      materialByKey: Map<string, THREE.Material>,
      modelOrigin: THREE.Vector3,
      layerId: string,
    ): BatchedMeshData[] => {
      // Group by material and apply relative offsets
      const geometriesByMaterial = new Map<string, THREE.BufferGeometry[]>();

      for (const { geometry, translation, materialKey } of pendingGeometries) {
        const relativeOffset = translation.clone().sub(modelOrigin);
        geometry.translate(relativeOffset.x, relativeOffset.y, relativeOffset.z);

        if (!geometriesByMaterial.has(materialKey)) {
          geometriesByMaterial.set(materialKey, []);
        }
        geometriesByMaterial.get(materialKey)!.push(geometry);
      }

      // Merge and create batched meshes
      const result: BatchedMeshData[] = [];
      let batchIndex = 0;

      for (const [materialKey, geometries] of geometriesByMaterial) {
        if (geometries.length === 0) continue;

        const material = materialByKey.get(materialKey)!;

        for (let i = 0; i < geometries.length; i += MERGE_CHUNK_SIZE) {
          const chunk = geometries.slice(i, i + MERGE_CHUNK_SIZE);
          const mergedGeometry = mergeGeometries(chunk, false);

          if (mergedGeometry) {
            result.push({
              geometry: mergedGeometry,
              material,
              layerId,
              id: `ifc-batch-${layerId}-${batchIndex++}`,
              origin: modelOrigin.clone(),
            });
          }

          chunk.forEach((g) => g.dispose());
        }
      }

      return result;
    },
    [],
  );

  // Stable key derived from layer IDs + roots so the effect only re-runs when IFC layers actually change
  const layersKey = useMemo(() => layers.map((l) => `${l.id}:${l.root}`).join(','), [layers]);

  // Stable key for the layer settings that matter (only IFC layer settings)
  const layerSettingsKey = useMemo(
    () =>
      layers
        .map((l) => {
          const s = layerSettings[l.id];
          return s ? `${l.id}:${s.modelDefaultColor}:${s.modelIsWireframe}` : l.id;
        })
        .join(','),
    [layers, layerSettings],
  );

  useEffect(() => {
    const abortController = new AbortController();
    setBatchedMeshes([]);

    const loadIFC = async (file: string, layerId: string) => {
      try {
        await initPromise;

        const response = await fetch(`${import.meta.env.VITE_RESOURCES_ENDPOINT}/${file}`, {
          signal: abortController.signal,
          credentials: 'include',
        });
        const data = await response.arrayBuffer();

        if (abortController.signal.aborted) return;

        const layerSetting = layerSettings[layerId];
        if (!layerSetting) return;

        const modelID = ifcApi.OpenModel(new Uint8Array(data), {
          COORDINATE_TO_ORIGIN: false,
          CIRCLE_SEGMENTS: 6,
          TOLERANCE_PLANE_INTERSECTION: 1.0e-4,
          TOLERANCE_PLANE_DEVIATION: 1.0e-4,
          TOLERANCE_BACK_DEVIATION_DISTANCE: 1.0e-4,
          TOLERANCE_INSIDE_OUTSIDE_PERIMETER: 1.0e-10,
          TOLERANCE_SCALAR_EQUALITY: 1.0e-4,
          PLANE_REFIT_ITERATIONS: 3,
          BOOLEAN_UNION_THRESHOLD: 100,
        });

        const pendingGeometries: PendingGeometry[] = [];
        const materialByKey = new Map<string, THREE.Material>();
        let modelOrigin: THREE.Vector3 | null = null;

        ifcApi.StreamAllMeshes(modelID, (mesh: WebIFC.FlatMesh) => {
          if (abortController.signal.aborted) return;

          for (let i = 0; i < mesh.geometries.size(); i++) {
            const result = processPlacedGeometry(modelID, mesh.geometries.get(i), layerSetting);

            // Use first translation as origin
            if (!modelOrigin) {
              modelOrigin = result.translation.clone();
            }

            if (!materialByKey.has(result.materialKey)) {
              materialByKey.set(result.materialKey, result.material);
            }

            pendingGeometries.push({
              geometry: result.geometry,
              translation: result.translation,
              materialKey: result.materialKey,
            });
          }
        });

        if (abortController.signal.aborted) {
          ifcApi.CloseModel(modelID);
          return;
        }

        const finalOrigin = modelOrigin || new THREE.Vector3();
        const newBatchedMeshes = mergeGeometriesByMaterial(pendingGeometries, materialByKey, finalOrigin, layerId);

        if (!abortController.signal.aborted) {
          setBatchedMeshes((previousBatches) => [...previousBatches, ...newBatchedMeshes]);
        }

        ifcApi.CloseModel(modelID);
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error loading IFC:', error);
        }
      }
    };

    layers.forEach((layer) => loadIFC(layer.root!, layer.id));

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey, layerSettingsKey, processPlacedGeometry, mergeGeometriesByMaterial]);

  useEffect(() => {
    return () => {
      batchedMeshes.forEach(({ geometry }) => geometry.dispose());
    };
  }, [batchedMeshes]);

  // Group meshes by layerId to render each model with its own origin
  const meshesByLayer = useMemo(() => {
    const grouped = new Map<string, { meshes: BatchedMeshData[]; origin: THREE.Vector3 }>();
    for (const mesh of batchedMeshes) {
      if (!grouped.has(mesh.layerId)) {
        grouped.set(mesh.layerId, { meshes: [], origin: mesh.origin });
      }
      grouped.get(mesh.layerId)!.meshes.push(mesh);
    }
    return grouped;
  }, [batchedMeshes]);

  return (
    <>
      {Array.from(meshesByLayer.entries())
        .filter(([layerId]) => !!layerSettings[layerId])
        .map(([layerId, { meshes, origin }]) => (
          <group key={layerId} position={origin}>
            {meshes.map(({ geometry, material, id }) => (
              <mesh key={id} geometry={geometry} material={material} />
            ))}
          </group>
        ))}
    </>
  );
};
